'use client';
import { withAuth } from '@/components/auth/WithAuth';
import PageContainer from '@/components/layout/PageContainer';
import { useSetMobileHeader } from '@/components/layout/MobileHeaderProvider';
import MobileHeader from '@/components/header/me-profile/Header';
import Button from '@/components/ui/button/submit/Button';
import { Tab } from '@/components/ui/tab/Tab';
import CardViewWithData from '@/components/ui/cardView/CardViewWithData';
import { useIsMounted } from '@/hooks/useIsMounted';
import HeaderContent from '@/components/header/me-profile/HeaderContent';
import BottomNav from '@/components/bottomNav/BottomNav';
import ArticleList from '@/components/page/article/ArticleList';
import { useQueryEnumParam } from '@/hooks/useQueryEnumParam';
import ArSpaceList from '@/components/page/ar-space/ArSpaceList';
import { useEffect, useState } from 'react';
import { MeService } from '@/libs/services/me';
import { showApiError } from '@/libs/helpers/showApiState';
import { useAppDispatch } from '@/store/hook';
import { startGlobalLoading, stopGlobalLoading } from '@/features/ui/uiLoadingSlice';
import type { MeProfile } from '@/types/meProfile';
import { useArSpaceNavigation } from '@/hooks/useArSpaceNavigation';

type TabType = 'ARspace' | 'gamePost';

const PageClient = () => {
  const mounted = useIsMounted();
  const dispatch = useAppDispatch();
  useArSpaceNavigation(); //AR關閉路由設定

  useSetMobileHeader(MobileHeader);
  const [tab, setTab] = useQueryEnumParam<TabType>({
    paramName: 'tabPage',
    validValues: ['ARspace', 'gamePost'] as const,
    defaultValue: 'ARspace',
  });
  const [meInfo, setMeInfo] = useState<MeProfile>();
  const myUserId = meInfo?.user_id;

  useEffect(() => {
    const getMeInfo = async () => {
      dispatch(startGlobalLoading());
      try {
        const meInfoRawData = await MeService.getMeInfo();
        setMeInfo(meInfoRawData);
      } catch (err) {
        showApiError(err, '取得個人資訊失敗');
      } finally {
        dispatch(stopGlobalLoading());
      }
    };
    getMeInfo();
  }, [dispatch]);

  if (!mounted) {
    return null;
  }

  return (
    <>
      <PageContainer maxWidth="xl">
        <div>
          {/* 桌機顯示區 */}
          <div className="d-none d-md-block">
            <HeaderContent avatar={{ size: 100, className: 'me-3' }} />
          </div>

          {/* 數據區 */}
          <section className="d-flex mb-10">
            <CardViewWithData title="住進我空間的人次">
              <span className="fw-bold fs-3 my-2 text-center">{meInfo?.my_ar_space_num}</span>
              <div className="ms-auto">
                <Button as="a" href="/me/ar-space/new" color="red" size="md">
                  ＋空間
                </Button>
              </div>
            </CardViewWithData>

            <CardViewWithData title="擁有我籌碼的好友">
              <span className="fw-bold fs-3 my-2 text-center">{meInfo?.have_my_coin_num}</span>
              <div className="ms-auto">
                <Button as="a" href="/me/article/new" color="green" size="md">
                  ＋貼文
                </Button>
              </div>
            </CardViewWithData>
          </section>

          <section className="mb-5">
            <Tab
              isActive={tab === 'ARspace'}
              onClick={() => setTab('ARspace')}
              isShowData={true}
              num={meInfo?.ar_num}
            >
              AR空間
            </Tab>
            <Tab
              isActive={tab === 'gamePost'}
              onClick={() => setTab('gamePost')}
              isShowData={true}
              num={meInfo?.article_num}
            >
              互動貼文
            </Tab>
          </section>

          {/* 貼文列表區 */}
          {tab === 'gamePost' && myUserId && (
            <ArticleList kind="article" filterKind="kol" filterId={myUserId} />
          )}

          {/* AR列表區 */}

          {tab === 'ARspace' && meInfo?.womo_id && (
            <ArSpaceList
              filterKind="kol"
              filterId={meInfo?.womo_id}
              listClassName="row row-cols-2 row-cols-md-3 g-4"
            />
          )}
        </div>
      </PageContainer>

      {/* 置底按鈕選單 */}
      <BottomNav />
    </>
  );
};

export default withAuth(PageClient);
